import {IQueuedPdfServerJob} from '@modules/pdf'
import {CatalogInstanceChildStore, CatalogStore} from '@stores/catalog'
import {GlobalDeploymentSettings} from '@stores/config'
import {site} from '@stores/site'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {IUserSettings} from '@stores/user/UserStore'
import {authStore} from '@utils'
import {action, autorun, computed, observable, reaction, runInAction, toJS} from 'mobx'
import {computedFn, jsondiff, lockr, stringify, _} from '@api'

export const defaultUserSettings: Partial<IUserSettings> = {
  autoSaveReports: true,
  focusMode: {
    enabled: false,
    footers: false,
    tabs: false,
    toolbars: false
  },
  sync: {showSnackbar: false},
  dev: DEBUG ? {
    localNest: false,
    localSAM: false,
  } : undefined,
}

export class CurrentUserStore extends CatalogInstanceChildStore {
  constructor(public catalog: CatalogStore) {
    super(catalog)
    synchronizeStores(() => {
      if (!catalog.isGlobalStore) throw new Error(`This class can only be used as a singleton in the global application store`)

      let firstSettingsLoad = true
      autorun(async () => {
        const {authStore: {user}, workspaces: {deploymentConfig}} = this.stores
        if (user && deploymentConfig) {
          await this.loadUserSettings()
        }
      }, {name: `Load user settings`})

      autorun(() => {
        const {workspaces: {availableWorkspaces}, currentUser: {userSettings}} = this.stores
        if (userSettings) {
          if (!userSettings.workspaces) {
            userSettings.workspaces = {}
          }
          availableWorkspaces.forEach(({_id}) => {
            if (!userSettings.workspaces[_id]) {
              userSettings.workspaces[_id] = {}
            }

            if (!userSettings.workspaces[_id].notifications) {
              userSettings.workspaces[_id].notifications = {desktop: true, email: false, phone: false}
            }
          })
        }
      }, {name: `Initialize notification settings`})

      autorun(() => {
        const {stores: {deployments: {deploymentConfig}}, userSettings: {showC8Images}} = this
        if (deploymentConfig && _.isBoolean(showC8Images)) {
          runInAction(() => {
            _.merge(deploymentConfig, {settings: { images: {alwaysUseC8: showC8Images}} as Partial<GlobalDeploymentSettings> })
          })
        }
      })

      autorun(async () => {
        if (this._hasLoadedSettingsFromEs) {
          const {userSettings} = this,
            clone = _.cloneDeep(userSettings)

          if (firstSettingsLoad) {
            firstSettingsLoad = false
          } else {
            try {
              await this._saveUserSettings_debounced()
            } catch (err) {
              console.error(err)
            }
          }
        }
      }, { name: `Save user settings`})

      this.allowAutoruns = true
    })
  }

  /**
   * Set to true after startup logic
   */
  @observable allowAutoruns = false

  get info() { return authStore.user }

  @observable private saveUserSettingChanges = false
  @observable userSettings: Partial<IUserSettings> = defaultUserSettings

  @observable private _hasLoadedSettingsFromEs = false

  @computed get settingsUserId() {
    const {stores: {users: {current}}} = this
    if (DEBUG) return `${current?.id}-${BUILD_USER}`

    return current?.id
  }

  @action loadUserSettings = async () => {
    const {es, settingsUserId} = this
    try {
      let r = await es.get({index: es.indexes.userSettings, id: settingsUserId, ignoreError: true}, {log: {query: false}})

      runInAction(`Save user settings from ES`, () => {
        _.merge(this.userSettings, {
          workspaces: {},
          ...(r?.settings ? JSON.parse(r.settings) : {})
        })
        this.originalSettings = _.cloneDeep(this.userSettings)
        this._hasLoadedSettingsFromEs = true
      })
    }
    catch (err) {
      site.onError(err)
      this._hasLoadedSettingsFromEs = true
    }
  }

  @observable.ref originalSettings

  @computed get areUserSettingsDirty() {
    const diff = jsondiff.diff(this.userSettings, this.originalSettings)
    if (diff) {
      // console.log(`User global settings changed:  `, diff)
      return true
    }

    return false
  }

  _saveUserSettings_debounced = _.debounce(async () => {
    await this.saveUserSettings()
  }, 1000)

  /**
   * Global user settings
   * @param force
   */
  @action saveUserSettings = async (force = false) => {
    const {authStore: {user}, settingsUserId, es} = this
    if (user) {
      const {userSettings, areUserSettingsDirty} = this
      if (areUserSettingsDirty || force)
        await es.genericUpsert({
          index: es.indexes.userSettings,
          _id: settingsUserId,
          body: {settings: stringify(_.omit(userSettings, ''))}
        })
    }
  }

  @computed get hasLoadedSettings() {
    return this._hasLoadedSettingsFromEs
  }

  @action toggleServerPDF = () => {
    this.userSettings.serverPDF = this.userSettings.serverPDF !== true
  }

  @action toggleC8ImageSource = () => {
    this.userSettings.showC8Images = this.userSettings.showC8Images !== true
  }

  @action toggleLocalNest = () => {
    _.assign(this.userSettings, {dev: { localNest: this.userSettings?.dev?.localNest !== true}})
  }
  @action toggleLocalSAM = () => {
    _.assign(this.userSettings, {dev: {localSAM: this.userSettings?.dev?.localSAM !== true}})
  }

  @action toggleVirtualCatalog = () => {
    _.assign(this.userSettings, {dev: {virtualizedCatalog: this.userSettings?.dev?.virtualizedCatalog !== true}})
  }


  @observable pendingPdfJobs = observable.array<IQueuedPdfServerJob>()

  @computed get allFocusChecked () {
    const {userSettings: {focusMode}, anyFocusModeItemsExplicitlyChecked} = this
    return focusMode.enabled && !anyFocusModeItemsExplicitlyChecked
  }

  @action toggleFocusMode = (mode: 'button' | 'tabs' | 'toolbars' | 'footers') => e => {
    const {userSettings: {focusMode}, anyFocusModeItemsExplicitlyChecked, allFocusChecked} = this
    if (mode != 'button' && allFocusChecked) {
      _.merge(focusMode, {footers: true, tabs: true, toolbars: true})
    }

    switch (mode) {
      case 'button':
        focusMode.enabled = !focusMode.enabled
        break;
      case 'tabs':
        focusMode.tabs = !focusMode.tabs

        break;
      case 'toolbars':
        focusMode.toolbars = !focusMode.toolbars
        break;
      case 'footers':
        focusMode.footers = !focusMode.footers
        break;
    }

    if (mode != 'button') {
      focusMode.enabled = this.anyFocusModeItemsExplicitlyChecked
    }
  }

  @computed get anyFocusModeItemsExplicitlyChecked() {
    return ['tabs', 'toolbars', 'footers'].some(v => this.userSettings.focusMode?.[v])
  }

  @computed get focus_hideToolbars() {
    const {userSettings: {focusMode}, anyFocusModeItemsExplicitlyChecked} = this
    return focusMode.enabled && (focusMode.toolbars || !anyFocusModeItemsExplicitlyChecked)
  }
}


// WEBPACK FOOTER //
// ./src/stores/user/CurrentUserStore.ts